import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ENABLE_ORGANISATIONS } from "@/lib/featureFlags";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import MentorOnboardingPage from "./pages/MentorOnboardingPage";
import MentorListingPage from "./pages/MentorListingPage";
import MentorProfilePage from "./pages/MentorProfilePage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminProfilePage from "./pages/AdminProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OrganisationListingPage from "./pages/OrganisationListingPage";
import OrganisationProfilePage from "./pages/OrganisationProfilePage";
import OrganisationSignupPage from "./pages/OrganisationSignupPage";
import InstituteDashboardPage from "./pages/InstituteDashboardPage";
import ChatPage from "./pages/ChatPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import NotFound from "./pages/NotFound";
import UnsubscribePage from "./pages/UnsubscribePage";
import NotificationManager from "./components/NotificationManager";
import EnableNotificationsPrompt from "./components/EnableNotificationsPrompt";

// Lazy-loaded community pages (zero impact on main bundle)
const CommunityFeedPage = lazy(() => import("./pages/CommunityFeedPage"));
const CreatePostPage = lazy(() => import("./pages/CreatePostPage"));
const PostDetailPage = lazy(() => import("./pages/PostDetailPage"));
const EditPostPage = lazy(() => import("./pages/EditPostPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <NotificationManager />
          <EnableNotificationsPrompt />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/role-selection" element={<RoleSelectionPage />} />
            <Route path="/mentor-onboarding" element={<MentorOnboardingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/mentors" element={<MentorListingPage />} />
            <Route path="/mentors/:id" element={<MentorProfilePage />} />
            {ENABLE_ORGANISATIONS ? (
              <>
                <Route path="/organisations" element={<OrganisationListingPage />} />
                <Route path="/organisations/register" element={<OrganisationSignupPage />} />
                <Route path="/organisations/:slug" element={<OrganisationProfilePage />} />
                <Route path="/institute/dashboard" element={<InstituteDashboardPage />} />
              </>
            ) : (
              <>
                <Route path="/organisations" element={<Navigate to="/mentors" replace />} />
                <Route path="/organisations/*" element={<Navigate to="/mentors" replace />} />
                <Route path="/institute/*" element={<Navigate to="/mentors" replace />} />
              </>
            )}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/booking-confirmed/:bookingId" element={<BookingConfirmationPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/profile" element={<AdminProfilePage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/community" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><CommunityFeedPage /></Suspense>} />
            <Route path="/community/new" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><CreatePostPage /></Suspense>} />
            <Route path="/community/:id" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><PostDetailPage /></Suspense>} />
            <Route path="/community/:id/edit" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><EditPostPage /></Suspense>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
